<template>
  <div class="dashboard">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ greeting }}, {{ userName }}</h1>
        <p class="page-sub">{{ t('dashboard.sub') }}</p>
      </div>
      <button class="btn btn-primary" @click="openNew()">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        {{ t('dashboard.newTask') }}
      </button>
    </div>

    <!-- Stats -->
    <div class="stat-strip">
      <div class="stat">
        <div class="stat-value">{{ stats.open }}</div>
        <div class="stat-label">{{ t('dashboard.open') }}</div>
      </div>
      <div class="stat">
        <div class="stat-value">{{ stats.in_progress }}</div>
        <div class="stat-label">{{ t('dashboard.inProgress') }}</div>
      </div>
      <div class="stat">
        <div class="stat-value">{{ stats.done }}</div>
        <div class="stat-label">{{ t('dashboard.done') }}</div>
      </div>
      <div class="stat stat-crit">
        <div class="stat-value">{{ stats.critical }}</div>
        <div class="stat-label">{{ t('dashboard.critical') }}</div>
      </div>
    </div>

    <!-- Customize bar: drag hint + chips to re-add hidden widgets -->
    <div class="customize-bar">
      <span class="cust-hint">{{ t('dashboard.dragHint') }}</span>
      <span class="cust-spacer"></span>
      <button
        v-for="key in hidden"
        :key="'add' + key"
        class="cust-chip"
        @click="addWidget(key)"
        :title="t('dashboard.addWidget')"
      >+ {{ widgetTitle(key) }}</button>
    </div>

    <div v-if="loading" class="spinner"></div>

    <div v-else-if="board.length === 0" class="empty-state">
      <p>{{ t('dashboard.noWidgets') }}</p>
    </div>

    <!-- Draggable widget board -->
    <draggable
      v-else
      v-model="board"
      :item-key="k => k"
      handle=".w-grip"
      class="widget-board"
      :animation="160"
      @end="onDragEnd"
    >
      <template #item="{ element: key }">
        <section class="widget" :class="'w-' + key">
          <header class="widget-head">
            <span class="w-grip" :title="t('dashboard.dragHint')">⠿</span>
            <h2 class="widget-title">{{ widgetTitle(key) }}</h2>
            <span class="widget-count" v-if="widgetCount(key) !== null">{{ widgetCount(key) }}</span>
            <span class="w-spacer"></span>
            <router-link v-if="widgetLink(key)" :to="widgetLink(key)" class="w-link">{{ t('common.viewAll') }} →</router-link>
            <button class="w-hide" :title="t('dashboard.hideWidget')" @click="removeWidget(key)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </header>

          <div class="widget-body">
            <!-- My Tasks -->
            <template v-if="key === 'mytasks'">
              <p v-if="!myTasks.length" class="w-empty">{{ t('dashboard.empty') }}</p>
              <div v-else class="w-tasks">
                <TaskCard
                  v-for="task in myTasks.slice(0, 6)"
                  :key="task.id"
                  :task="task"
                  :users="users"
                  compact
                  @open="openTask"
                  @complete="completeTask"
                  @reopen="reopenTask"
                  @restore="restoreTask"
                  @assign="assignTask"
                  @timer-update="onTimerUpdate"
                />
              </div>
            </template>

            <!-- Projects + progress -->
            <template v-else-if="key === 'projects'">
              <p v-if="!projects.length" class="w-empty">{{ t('dashboard.noProjects') }}</p>
              <router-link
                v-for="p in projects"
                :key="p.id"
                :to="`/tasks?project_id=${p.id}`"
                class="w-proj"
              >
                <span class="w-proj-top">
                  <span class="w-dot" :style="{ background: p.color }"></span>
                  <span class="w-proj-name">{{ p.name }}</span>
                  <span class="w-proj-count">{{ p.task_count }}</span>
                </span>
                <span v-if="pct(p.id) !== null" class="w-bar">
                  <span class="w-bar-fill" :style="{ width: pct(p.id) + '%', background: p.color }"></span>
                </span>
              </router-link>
            </template>

            <!-- Subtasks -->
            <template v-else-if="key === 'subtasks'">
              <p v-if="!feed.subtasks.length" class="w-empty">{{ t('dashboard.noSubtasks') }}</p>
              <div v-for="s in feed.subtasks" :key="s.id" class="w-sub">
                <span class="w-check" :class="{ on: s.is_done }">
                  <svg v-if="s.is_done" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
                </span>
                <span class="w-sub-title" :class="{ done: s.is_done }">{{ s.title || '—' }}</span>
                <span class="w-sub-task">{{ s.task_title }}</span>
              </div>
            </template>

            <!-- Clients -->
            <template v-else-if="key === 'clients'">
              <p v-if="!clients.length" class="w-empty">{{ t('clients.empty') }}</p>
              <div v-for="c in clients.slice(0, 8)" :key="c.id" class="w-client">
                <span class="w-ini">{{ initials(c.name) }}</span>
                <span class="w-client-name">{{ c.name }}</span>
                <span class="w-client-tags">
                  <span v-for="(p, i) in (c.projects || []).slice(0, 2)" :key="i" class="w-tag">{{ p }}</span>
                </span>
              </div>
            </template>

            <!-- Comments -->
            <template v-else-if="key === 'comments'">
              <p v-if="!feed.comments.length" class="w-empty">{{ t('dashboard.noComments') }}</p>
              <div v-for="c in feed.comments" :key="c.id" class="w-comment">
                <span class="w-comment-task">{{ c.task_title }}</span>
                <span class="w-comment-body">{{ c.body }}</span>
              </div>
            </template>

            <!-- Requests -->
            <template v-else-if="key === 'requests'">
              <p v-if="!pendingRequests.length" class="w-empty">{{ t('dashboard.noRequests') }}</p>
              <router-link
                v-for="r in pendingRequests.slice(0, 6)"
                :key="r.id"
                to="/requests"
                class="w-req"
              >
                <span class="w-req-title">{{ r.title }}</span>
                <span class="status-pill st-pending">{{ t('requests.pending') }}</span>
              </router-link>
            </template>
          </div>
        </section>
      </template>
    </draggable>

    <TaskFormModal
      v-if="showForm"
      :task="editingTask"
      :projects="projects"
      :defaults="formDefaults"
      @close="closeForm"
      @saved="onSaved"
    />

    <TaskDetailDrawer
      v-if="drawerTask"
      :task="drawerTask"
      :projects="projects"
      @close="drawerTask = null"
      @saved="onDrawerSaved"
      @changed="refresh"
      @complete="completeTask"
      @reopen="reopenTask"
      @restore="restoreTask"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import draggable     from 'vuedraggable'
import TaskCard      from '@/components/TaskCard.vue'
import TaskFormModal from '@/components/TaskFormModal.vue'
import TaskDetailDrawer from '@/components/TaskDetailDrawer.vue'
import { useAuth }   from '@/composables/useAuth'
import { useI18n }   from '@/composables/useI18n'
import { useToast }  from '@/composables/useToast'
import { onRealtime, debounce } from '@/composables/useRealtime'
import api from '@/api'

const ALL_WIDGETS = ['mytasks', 'projects', 'subtasks', 'clients', 'comments', 'requests']

export default {
  name: 'DashboardView',
  components: { draggable, TaskCard, TaskFormModal, TaskDetailDrawer },
  setup() {
    const { currentUser } = useAuth()
    const { t }           = useI18n()
    const toast           = useToast()

    const myTasks  = ref([])
    const projects = ref([])
    const clients  = ref([])
    const users    = ref([])
    const requests = ref([])
    const feed     = reactive({ comments: [], subtasks: [] })
    const projectTasks = reactive({})

    const loading  = ref(true)
    const showForm = ref(false)
    const editingTask  = ref(null)
    const formDefaults = ref({})
    const drawerTask   = ref(null)

    // Draggable board (visible, ordered) + hidden widget keys. Persisted per user.
    const board  = ref([...ALL_WIDGETS])
    const hidden = ref([])

    const userName = computed(() => currentUser()?.name?.split(' ')[0] || t('dashboard.there'))
    const greeting = computed(() => {
      const h = new Date().getHours()
      if (h < 12) return t('dashboard.morning')
      if (h < 17) return t('dashboard.afternoon')
      return t('dashboard.evening')
    })

    const stats = computed(() => ({
      open:        myTasks.value.filter(t => t.status === 'open').length,
      in_progress: myTasks.value.filter(t => t.status === 'in_progress').length,
      done:        myTasks.value.filter(t => t.status === 'done').length,
      critical:    myTasks.value.filter(t => t.urgency === 'critical' && t.status !== 'done').length,
    }))

    const pendingRequests = computed(() =>
      requests.value.filter(r => r.status === 'pending' || r.status === 'revised')
    )

    function pct(pid) {
      const ts = projectTasks[pid]
      if (!ts || ts.length === 0) return null
      return Math.round(ts.filter(t => t.status === 'done').length / ts.length * 100)
    }

    const WIDGET_TITLES = {
      mytasks: 'dashboard.myTasks', projects: 'dashboard.projects', subtasks: 'dashboard.subtasksWidget',
      clients: 'nav.clients', comments: 'dashboard.commentsWidget', requests: 'nav.requests',
    }
    const widgetTitle = (key) => t(WIDGET_TITLES[key] || key)

    function widgetCount(key) {
      switch (key) {
        case 'mytasks':  return myTasks.value.length
        case 'projects': return projects.value.length
        case 'subtasks': return feed.subtasks.length
        case 'clients':  return clients.value.length
        case 'comments': return feed.comments.length
        case 'requests': return pendingRequests.value.length
        default: return null
      }
    }
    function widgetLink(key) {
      switch (key) {
        case 'mytasks':  return '/tasks'
        case 'projects': return '/projects'
        case 'clients':  return '/clients'
        case 'requests': return '/requests'
        default: return null
      }
    }

    const initials = (name) => (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

    // ─── layout persistence ───────────────────────────────────────
    async function loadLayout() {
      try {
        const res = (await api.getDashboardLayout()).data
        const saved = res && res.layout
        if (saved && Array.isArray(saved.board)) {
          const savedHidden = Array.isArray(saved.hidden) ? saved.hidden : []
          // Reconcile against the known widget set: keep saved order, append any
          // brand-new widgets, drop any that no longer exist.
          const known = new Set(ALL_WIDGETS)
          board.value  = saved.board.filter(k => known.has(k))
          hidden.value = savedHidden.filter(k => known.has(k))
          for (const k of ALL_WIDGETS) {
            if (!board.value.includes(k) && !hidden.value.includes(k)) board.value.push(k)
          }
        }
      } catch {}
    }
    const persist = debounce(() => {
      api.saveDashboardLayout({ board: board.value, hidden: hidden.value }).catch(() => {})
    }, 300)

    function removeWidget(key) {
      board.value = board.value.filter(k => k !== key)
      if (!hidden.value.includes(key)) hidden.value.push(key)
      persist()
    }
    function addWidget(key) {
      hidden.value = hidden.value.filter(k => k !== key)
      if (!board.value.includes(key)) board.value.push(key)
      persist()
    }

    // ─── data ─────────────────────────────────────────────────────
    async function loadProjectTasks(pid) {
      try { projectTasks[pid] = (await api.getTasks({ project_id: pid })).data || [] }
      catch { projectTasks[pid] = [] }
    }
    async function load() {
      try {
        const [mr, pr, cr, rr, fr] = await Promise.all([
          api.getTasks({ mine: 1 }),
          api.getProjects(),
          api.getClients().catch(() => ({ data: [] })),
          api.getRequests().catch(() => ({ data: [] })),
          api.getDashboardFeed().catch(() => ({ data: { comments: [], subtasks: [] } })),
        ])
        myTasks.value  = mr.data || []
        projects.value = pr.data || []
        clients.value  = cr.data || []
        requests.value = rr.data || []
        feed.comments  = (fr.data && fr.data.comments) || []
        feed.subtasks  = (fr.data && fr.data.subtasks) || []
        await Promise.all(projects.value.map(p => loadProjectTasks(p.id)))
      } catch {} finally {
        loading.value = false
      }
    }
    async function refresh() { await load() }

    // ─── task actions (My Tasks widget) ───────────────────────────
    // Completing archives the task (consistent with the tasks board).
    async function completeTask(task) { try { await api.archiveTask(task.id); drawerTask.value = null; await refresh(); toast.success(t('toast.taskCompleted')) } catch { toast.error(t('toast.actionFailed')) } }
    async function reopenTask(task)   { try { await api.reopenTask(task.id);   await refresh(); toast.success(t('toast.taskReopened')) } catch { toast.error(t('toast.actionFailed')) } }
    async function restoreTask(task)  { try { await api.restoreTask(task.id); drawerTask.value = null; await refresh(); toast.success(t('toast.taskRestored')) } catch { toast.error(t('toast.actionFailed')) } }
    async function loadUsers() { try { users.value = (await api.getUsers()).data || [] } catch {} }
    async function assignTask({ task, userId }) {
      const prev = task.assigned_to
      const name = users.value.find(u => u.id === userId)?.name || null
      task.assigned_to = userId
      task.assignee_name = name
      try {
        await api.updateTask(task.id, { assigned_to: userId })
        toast.success(userId ? t('toast.assigned', { name }) : t('toast.unassigned'))
      } catch { task.assigned_to = prev; await refresh(); toast.error(t('toast.actionFailed')) }
    }
    function openTask(task) { drawerTask.value = task }
    function onDrawerSaved() { drawerTask.value = null; refresh() }
    function openNew()      { editingTask.value = null; formDefaults.value = {}; showForm.value = true }
    function closeForm()    { showForm.value = false; editingTask.value = null; formDefaults.value = {} }
    function onSaved()      { closeForm(); refresh() }

    function onTimerUpdate(payload) {
      const lists = [myTasks.value, ...Object.values(projectTasks)]
      for (const list of lists) {
        const tk = list.find(t => t.id === payload.taskId)
        if (tk) {
          tk.timer_seconds    = payload.timer_seconds
          tk.timer_started_at = payload.timer_started_at
          if (payload.status) tk.status = payload.status
        }
      }
    }

    // ─── masonry packing ──────────────────────────────────────────
    // Size each widget's grid row-span to its height so columns pack tightly
    // upward (no gaps). DOM order is untouched, so vuedraggable still works.
    const ROW_GAP = 16
    let ro = null
    function layoutMasonry() {
      const boardEl = document.querySelector('.widget-board')
      if (!boardEl) return
      boardEl.querySelectorAll('.widget').forEach(w => {
        w.style.gridRowEnd = 'span ' + Math.ceil(w.getBoundingClientRect().height + ROW_GAP)
      })
    }
    // (Re)observe each widget so content-height changes (data loading in,
    // collapsing/expanding) re-pack the board automatically.
    function observeWidgets() {
      if (!ro) return
      ro.disconnect()
      document.querySelectorAll('.widget-board .widget').forEach(w => ro.observe(w))
      layoutMasonry()
    }
    async function onDragEnd() { persist(); await nextTick(); layoutMasonry() }

    let offRealtime = () => {}
    onMounted(async () => {
      ro = new ResizeObserver(() => layoutMasonry())
      window.addEventListener('resize', layoutMasonry)
      await Promise.all([loadLayout(), load(), loadUsers()])
      await nextTick(); observeWidgets()
      offRealtime = onRealtime('task.changed', debounce(refresh, 400))
    })
    // Re-pack when the set/order of widgets changes (add, remove, reorder).
    watch(board, async () => { await nextTick(); observeWidgets() }, { deep: true })
    onUnmounted(() => {
      offRealtime()
      window.removeEventListener('resize', layoutMasonry)
      if (ro) ro.disconnect()
    })

    return {
      myTasks, projects, clients, feed, pendingRequests, projectTasks,
      board, hidden, loading, showForm, editingTask, formDefaults, drawerTask, users,
      userName, greeting, stats, t, pct, initials,
      widgetTitle, widgetCount, widgetLink, persist, removeWidget, addWidget, onDragEnd,
      completeTask, reopenTask, restoreTask, assignTask, openTask, onDrawerSaved, openNew, closeForm, onSaved, onTimerUpdate,
    }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }

/* Flat stat strip */
.stat-strip {
  display: grid; grid-template-columns: repeat(4, 1fr);
  border: 1.5px solid color-mix(in srgb, var(--accent-ink) 22%, var(--card-brd));
  border-radius: var(--radius);
  background: var(--surface); overflow: hidden; margin-bottom: 18px;
}
.stat { padding: 16px 20px; }
.stat + .stat { border-inline-start: 1px solid var(--border); }
.stat-value { font-variant-numeric: tabular-nums; font-size: var(--fs-display); font-weight: 600; line-height: 1; letter-spacing: -.02em; }
.stat-crit .stat-value { color: var(--danger); }
.stat-label { font-size: 11px; color: var(--muted); margin-top: 7px; }

/* Customize bar */
.customize-bar { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; flex-wrap: wrap; }
.cust-hint { font-size: 11.5px; color: var(--faint, var(--muted)); }
.cust-spacer { flex: 1; }
.cust-chip {
  font-size: 11.5px; font-weight: 600; color: var(--accent-ink);
  background: var(--accent-wash); border: 1px dashed color-mix(in srgb, var(--accent-ink) 40%, transparent);
  border-radius: 999px; padding: 4px 11px; cursor: pointer;
}
.cust-chip:hover { background: var(--accent); color: var(--on-accent); border-style: solid; }

/* Board grid — masonry: fine 1px rows + JS-set row-spans pack widgets of varying
   heights tightly upward (no gaps), while DOM order stays intact so drag works. */
.widget-board {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  grid-auto-rows: 1px; column-gap: 16px; row-gap: 0; align-items: start;
}
.widget {
  background: var(--surface);
  border: 1.5px solid color-mix(in srgb, var(--accent-ink) 22%, var(--card-brd));
  border-radius: var(--radius);
  overflow: hidden; display: flex; flex-direction: column;
  box-shadow: 0 2px 12px rgba(20,24,40,.07);
}
.widget-head { background: color-mix(in srgb, var(--accent-ink) 5%, var(--surface)); }
.widget.sortable-ghost { opacity: .5; }
.widget-head {
  display: flex; align-items: center; gap: 9px;
  padding: 12px 14px; border-bottom: 1px solid var(--border);
}
.w-grip { cursor: grab; color: var(--faint); font-size: 14px; line-height: 1; user-select: none; }
.w-grip:active { cursor: grabbing; }
.widget-title { font-size: 13.5px; font-weight: 700; color: var(--text); }
.widget-count {
  font-variant-numeric: tabular-nums; font-size: 11px; font-weight: 600; color: var(--muted);
  background: var(--surface-2); border: 1px solid var(--border); border-radius: 999px; padding: 1px 8px;
}
.w-spacer { flex: 1; }
.w-link { font-size: 11.5px; font-weight: 600; color: var(--accent-ink); text-decoration: none; white-space: nowrap; }
.w-link:hover { text-decoration: underline; }
.w-hide { border: none; background: none; cursor: pointer; color: var(--faint); display: flex; padding: 2px; }
.w-hide:hover { color: var(--danger); }

.widget-body { padding: 12px 14px; display: flex; flex-direction: column; gap: 8px; max-height: 420px; overflow: auto; }
.w-empty { font-size: 12.5px; color: var(--muted); padding: 8px 2px; }
.w-tasks { display: flex; flex-direction: column; gap: 8px; }

/* Projects */
.w-proj { display: flex; flex-direction: column; gap: 6px; text-decoration: none; padding: 7px 8px; border-radius: 8px; }
.w-proj:hover { background: var(--hover); }
.w-proj-top { display: flex; align-items: center; gap: 9px; }
.w-dot { width: 9px; height: 9px; border-radius: 3px; flex-shrink: 0; }
.w-proj-name { flex: 1; min-width: 0; font-size: 13px; font-weight: 500; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.w-proj-count { font-variant-numeric: tabular-nums; font-size: 11px; color: var(--muted); }
.w-bar { display: block; height: 4px; background: var(--surface-2); border-radius: 999px; overflow: hidden; }
.w-bar-fill { display: block; height: 100%; border-radius: 999px; transition: width .2s; }

/* Subtasks */
.w-sub { display: flex; align-items: center; gap: 9px; }
.w-check { width: 16px; height: 16px; border-radius: 5px; border: 1px solid var(--border-2, var(--border)); display: grid; place-items: center; color: var(--on-accent); flex-shrink: 0; }
.w-check.on { background: var(--accent); border-color: var(--accent); }
.w-sub-title { flex: 1; min-width: 0; font-size: 12.5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.w-sub-title.done { text-decoration: line-through; color: var(--muted); }
.w-sub-task { font-size: 10.5px; color: var(--faint); font-family: var(--font-mono); white-space: nowrap; }

/* Clients */
.w-client { display: flex; align-items: center; gap: 9px; }
.w-ini { width: 26px; height: 26px; flex-shrink: 0; border-radius: 6px; background: var(--surface-2); border: 1px solid var(--border); color: var(--muted); display: grid; place-items: center; font-size: 10.5px; font-weight: 600; }
.w-client-name { flex: 1; min-width: 0; font-size: 13px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.w-client-tags { display: flex; gap: 4px; }
.w-tag { font-size: 10px; padding: 1px 7px; border-radius: 999px; background: var(--accent-wash); border: 1px solid var(--border); color: var(--text); white-space: nowrap; }

/* Comments */
.w-comment { display: flex; flex-direction: column; gap: 2px; padding-bottom: 6px; border-bottom: 1px solid var(--border); }
.w-comment:last-child { border-bottom: none; }
.w-comment-task { font-size: 11px; font-weight: 600; color: var(--accent-ink); }
.w-comment-body { font-size: 12.5px; color: var(--text); }

/* Requests */
.w-req { display: flex; align-items: center; gap: 9px; text-decoration: none; padding: 7px 8px; border-radius: 8px; }
.w-req:hover { background: var(--hover); }
.w-req-title { flex: 1; min-width: 0; font-size: 13px; font-weight: 500; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.status-pill { font-size: 10.5px; font-weight: 600; padding: 2px 9px; border-radius: 999px; background: var(--surface-2); color: var(--muted); border: 1px solid var(--border); white-space: nowrap; }

@media (max-width: 900px) {
  .stat-strip { grid-template-columns: repeat(2, 1fr); }
  .stat:nth-child(3) { border-inline-start: none; }
  .widget-board { grid-template-columns: 1fr; }
}
</style>
